<template>
  <div class="card" @click="() => integration.id == IntegrationType.OpenAI ? handleClick() : null">
    <div class="card_header">
      <div class="section_icon" v-if="integration.id == IntegrationType.OpenAI">
        <IconIA />
        <a-tag color="purple" class="tag">All in One</a-tag>
      </div>
      <div class="card_header--icon" v-else-if="isHubspot">
        <HubspotIcon />
      </div>

      <a-popover
        trigger="click"
        @click.stop
        v-model:visible="showPopover"
        placement="bottom"
      >
        <template #content>
          <div class="more-popover">
            <a-button
              type="text"
              class="button"
              @click="handleViewDetails"
            >
              <EyeOutlined />

              Ver detalles
            </a-button>
            <a-button
              v-if="hasHubspotFunctionalityActive && isHubspot"
              type="text"
              class="button"
              @click.stop="handleConfigure"
            >
              <EditOutlined />
              
              Editar configuración
            </a-button>
            <a-button
              v-if="isHubspot && hasHubspotExport"
              type="text"
              class="button"
              @click.stop="handleHubspotContact(HubspotIntegrationType.Export)"
            >
              <ExportOutlined />
              
              Exportar contactos
            </a-button>
            <a-button
              v-if="isHubspot && hasHubspotImport"
              type="text"
              class="button"
              @click.stop="handleHubspotContact(HubspotIntegrationType.Import)"
            >
              <ImportIcon />
              
              Importar contactos
            </a-button>
          </div>
        </template>

        <a-button v-if="isHubspot && dataHubspot" class="button" style="padding: 0" @click.stop type="text">
          <MoreOutlined class="more-icon" />
        </a-button>
      </a-popover>
    </div>
    <h3>{{ integration.title }}</h3>
    <h4>{{ integration.type }}</h4>
    <p style="min-height: 85px">
      {{ integration.description }}
      <a-button
        v-if="isHubspot"
        type="link"
        class="button_link"
        @click.stop="openHubspotInfo = true"
      >      
        Documentación
        <IconExternalLink style="margin-left: 5px" active/>
      </a-button>
    </p>

    <a-divider type="horizontal" class="divider" />

    <footer>
      <template v-if="isConnected">
        
        <span class="footer_section" style="color: #48aa1e">
          <CheckCircleTwoTone class="icon" two-tone-color="#48aa1e" />
          
          Conectado
        </span>

        <span
          class="footer_section"
          style="color: #1677FF"
          v-if="isHubspot && isConnected"
        >
          <a-button
            v-if="hubspotEvents.length == 0 && !hasHubspotFunctionalityActive"
            type="link"
            class="button_link"
            @click.stop="handleConfigure"
          >
            <SettingOutlined class="icon" two-tone-color="#1677FF" />
            
            Configurar
          </a-button>
          <hubspot-events-popover v-else-if="hubspotEvents.length > 0" :events="hubspotEvents" />
          <!-- <a-button
            v-else
            type="link"
            class="button_link"
            @click.stop="handleConfigure"
          >
            <ClockCircleOutlined />
            {{ hubspotEvents.length }} eventos
          </a-button> -->
        </span>

        <!-- <SettingOutlined class="info icon" v-if="integration.id != IntegrationType.Hubspot"/> -->
      </template>

      <template v-else>
        <a-tooltip>
          <template #title v-if="isHubspot && portfoliosActives?.length == 0">
            Debe existir al menos una campaña para conectar con Hubspot
          </template>
          <a-button
            class="info"
            @click="handleClick()"
            :disabled="isHubspot && (portfoliosActives?.length == 0)"
          >
            Conectar
          </a-button>
        </a-tooltip>
      </template>
    </footer>
  </div>

  <HubspotDetailsModal
    v-model="hubspotDetailsModalOpen"
    :integration="dataHubspot"
  />

  <HubspotConfigurationModal
    v-model="hubspotConfigModalOpen"
    v-model:hubspotConfigModalActive="hubspotConfigModalActive"
    :integration="dataHubspot"
    @closeVisible="hubspotConfigModalActive = false"
    @updateEvents="getHubspotEvents"
  />

  <HubspotEventModal
    v-if="eventModalVisibleImport"
    :visible="eventModalVisibleImport"
    @update:visible="eventModalVisibleImport = $event"
    :type="typeEventHubspot"
    @save="handleEventSave"
  />

  <HubspotManualModal
    v-model:open="openHubspotInfo"
  />

</template>

<script setup lang="ts">
import { SettingOutlined, CheckCircleTwoTone, MoreOutlined, EyeOutlined, EditOutlined, ExportOutlined } from "@ant-design/icons-vue";
import { Integration, IntegrationType } from "@/@types/integrations";
import IconIA from "@/app/common/components/atoms/IconIA.vue";

// import { getDataLoggedUser } from "@/app/settings/services";
import { onMounted, ref } from "vue";
import HubspotIcon from "../icons/HubspotIcon.vue";
import { computed } from "vue";
import HubspotDetailsModal from "../modals/HubspotDetailsModal.vue";
import HubspotConfigurationModal from "../modals/HubspotConfigurationModal.vue";
import IconExternalLink from "@/app/common/components/atoms/IconExternalLink.vue";
import { hubspotManager } from "../../services/HubspotManager";
import HubspotEventsPopover from "@/app/common/components/organisms/HubspotEventsPopover.vue";
import { useStore } from "vuex";
import ImportIcon from "@/app/common/components/icons/ImportIcon.vue";
import { HubspotIntegrationType } from "../../utils/enums";
import HubspotEventModal from "../modals/HubspotEventModal.vue";
import { message } from "ant-design-vue";
import { fetchAllActivesPortfolios } from "@/app/entities/services";
import HubspotManualModal from "../modals/HubspotManualModal.vue";

const props = defineProps<{ integration: Integration; integrationInfo: any }>();

const emit = defineEmits<{ click: [integrationId: number] }>();

const showPopover = ref(false);
const hubspotDetailsModalOpen = ref(false);
const hubspotConfigModalOpen = ref(false);
const hubspotConfigModalActive = ref(false);
const hubspotEvents = ref<Array<any>>([]);
const openHubspotInfo = ref(false);

const handleClick = () => emit("click", props.integration.id);

const store = useStore();
const typeEventHubspot = ref<HubspotIntegrationType | null>(null);
const eventModalVisibleImport = ref(false);

// const dataUser = computed(() => store.getters['getDataUser']);
const hasHubspotFunctionalityActive = computed(() => store.getters['hasHubspotFunctionalityActive']);
const dataHubspot = computed(() => {
  return props.integrationInfo?.data_hubspot;
});

const hasHubspotImport = computed(() => {
  return store.getters.hasHubspotByCode(HubspotIntegrationType.Import);
});

const hasHubspotExport = computed(() => {
  return store.getters.hasHubspotByCode(HubspotIntegrationType.Export);
});

// const customerData = ref<{ hubspot_status?: number } | null>(null);
const isConnected = computed(() => {
  return props.integration.is_connected;
});

const isHubspot = computed(() => {
  return props.integration.id == IntegrationType.Hubspot;
})

const handleViewDetails = () => {
  hubspotDetailsModalOpen.value = true;
  showPopover.value = false;
}

const handleConfigure = () => {
  hubspotConfigModalOpen.value = true;
  hubspotConfigModalActive.value = true;
  showPopover.value = false;
}

const getHubspotEvents = async () => {
  const response = await hubspotManager.getHubspotConfig();
  if (!response?.functionalities) return [];

  hubspotEvents.value = response.functionalities.flatMap((f: any) => 
    f.events.map((el: any) => ({...el, functionality: f.code })) || []
  );
};

const handleHubspotContact = (type: HubspotIntegrationType) => {
  eventModalVisibleImport.value = true;
  typeEventHubspot.value = type;
  showPopover.value = false;
};

const handleEventSave = async (payload: any) => {
  try {
    const { formState, type } = payload;
    const response = await hubspotManager.saveHubspotEvent({
      code: type,
      event: {
        id: formState.id,
        scheduled: formState.scheduled ?? null,
        contacts: formState.contacts ?? [],
        portfolios: formState.portfolio ?? [],
        entities: formState.entity ?? [],
      }
    });
    console.log(response, 'response')
    message.success(response.message);
    getHubspotEvents();
    eventModalVisibleImport.value = false;
  } catch (error) {
    const messageError = error?.getErrorMessage?.() || "Ha ocurrido un error";

    message.error(messageError);
  }
};

const portfoliosActives = ref([]);

onMounted(async () => {
  // customerData.value = await getDataLoggedUser();
  if (isHubspot) {
    const response = await fetchAllActivesPortfolios();
    portfoliosActives.value = response;
  }
  getHubspotEvents();
});
</script>

<style scoped lang="sass">

.section_icon
  width: 100%
  display: flex
  justify-content: space-between
  .tag
    height: 23px
h3
  color: $text-primary
  font-size: 16px
  font-weight: 600

h4
  color: $text-primary
  font-size: 12px
  font-weight: 400

p
  color: $gray-8
  text-wrap: pretty

.more-popover
  display: flex
  flex-direction: column
  gap: 8px
  > button
    text-align: left
    width: 100%


.card
  padding: 10px 20px 20px 20px
  border-radius: 8px
  border: 1px solid rgba(0, 0, 0, 0.15)
  background: #FFF
  cursor: pointer
  transition: border-color 0.25s ease, box-shadow 0.25s ease

  &:hover
    border-color: $primary
    box-shadow: 0px 5px 12px 4px rgba(0, 0, 0, 0.09), 0px 3px 6px 0px rgba(0, 0, 0, 0.12), 0px 1px 2px -2px rgba(0, 0, 0, 0.16)

  *
    margin: 0

  &_header
    display: flex
    justify-content: space-between
    &--icon
      width: 70px
      height: 70px
      display: flex
      align-items: center
  img
    margin-bottom: 12px
    width: 50px

.divider
  background-color: rgba(0, 0, 0, 0.15)
  margin: 8px 0

.info
  color: $gray-8

.icon
  font-size: 20px

footer
  display: flex
  height: 24px
  flex-direction: row-reverse
  justify-content: space-between
  align-items: center

  .footer_section
    display: flex
    align-items: center
    gap: 8px

.button_link
  display: flex
  align-items: center
  padding: 0
</style>
